import { useCallback, useState } from 'react';
import { createContainer } from 'unstated-next';
import {
  createAccountObjectChangeObject,
  createCustomFieldChangeObject,
  createEmptyMember,
  createEmptyRule,
} from './ruleFactory';

const useTemplateStore = () => {
  // State

  const [eventTypeRules, setEventTypeRules] = useState([]);
  const [fieldsAndTemplates, setFieldsAndTemplates] = useState({});
  const [dirtyState, setDirtyState] = useState(false);
  const [templateName, _setTemplateName] = useState('');

  const makeDirty = useCallback(() => {
    if (!dirtyState) {
      setDirtyState(true);
    }
  }, [dirtyState]);

  const setTemplateName = useCallback(
    newName => {
      if (templateName !== newName) {
        _setTemplateName(newName);
        makeDirty();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [makeDirty]
  );

  // Utils

  const getCustomField = useCallback(
    changeObject => {
      const rulesForEvent = eventTypeRules.find(
        etr => etr.eventType === changeObject.eventType
      ).rules;
      return rulesForEvent[changeObject.ruleIndex][changeObject.fieldName];
    },
    [eventTypeRules]
  );

  const getAccountField = useCallback(
    changeObject => {
      const rulesForEvent = eventTypeRules.find(
        etr => etr.eventType === changeObject.eventType
      ).rules;
      const accountObject =
        rulesForEvent[changeObject.ruleIndex][changeObject.transactionType];
      return accountObject.objectMappings.find(
        m => m.object.index === changeObject.objectIndex
      ).field;
    },
    [eventTypeRules]
  );

  const getCustomFieldMember = useCallback(
    changeObject => {
      const field = getCustomField(changeObject);
      return field.members[changeObject.memberIndex];
    },
    [getCustomField]
  );

  const getAccountFieldMember = useCallback(
    changeObject => {
      const field = getAccountField(changeObject);
      return field.members[changeObject.memberIndex];
    },
    [getAccountField]
  );

  // Methods

  const changeCustomFieldSource = useCallback(
    changeObject => {
      const fieldMember = getCustomFieldMember(changeObject);
      fieldMember.source = changeObject.newValue;
      makeDirty();
    },
    [getCustomFieldMember, makeDirty]
  );

  const changeAccountFieldSource = useCallback(
    changeObject => {
      const fieldMember = getAccountFieldMember(changeObject);
      fieldMember.source = changeObject.newValue;
      makeDirty();
    },
    [getAccountFieldMember, makeDirty]
  );

  const changeCustomFieldValue = useCallback(
    changeObject => {
      const fieldMember = getCustomFieldMember(changeObject);
      fieldMember.value = changeObject.newValue;
      makeDirty();
    },
    [getCustomFieldMember, makeDirty]
  );

  const changeAccountFieldValue = useCallback(
    changeObject => {
      const fieldMember = getAccountFieldMember(changeObject);
      fieldMember.value = changeObject.newValue;
      makeDirty();
    },
    [getAccountFieldMember, makeDirty]
  );

  const changeCustomFieldDelimiter = useCallback(
    changeObject => {
      const fieldMember = getCustomFieldMember(changeObject);
      fieldMember.precedingDelimiter = changeObject.newValue;
      makeDirty();
    },
    [getCustomFieldMember, makeDirty]
  );

  const changeAccountFieldDelimiter = useCallback(
    changeObject => {
      const fieldMember = getAccountFieldMember(changeObject);
      fieldMember.precedingDelimiter = changeObject.newValue;
      makeDirty();
    },
    [getAccountFieldMember, makeDirty]
  );

  const changeAccountNumber = useCallback(
    changeObject => {
      const rulesForEvent = eventTypeRules.find(
        etr => etr.eventType === changeObject.eventType
      ).rules;
      const accountObject =
        rulesForEvent[changeObject.ruleIndex][changeObject.transactionType];
      accountObject.accountNumber = changeObject.newValue;
      makeDirty();
    },
    [eventTypeRules, makeDirty]
  );

  const addRule = useCallback(
    (eventType, index) => {
      const newRule = createEmptyRule(
        eventType,
        fieldsAndTemplates.accountingObjects
      );
      const rulesForEvent = eventTypeRules.find(
        r => r.eventType === eventType
      ).rules;
      rulesForEvent.splice(index, 0, newRule);
      rulesForEvent.forEach((r, i) => {
        r.index = i;
      });
      setEventTypeRules([...eventTypeRules]);
      makeDirty();
    },
    [eventTypeRules, fieldsAndTemplates, makeDirty]
  );

  const deleteRule = useCallback(
    (eventType, index) => {
      const rulesForEvent = eventTypeRules.find(
        r => r.eventType === eventType
      ).rules;
      rulesForEvent.splice(index, 1);
      rulesForEvent.forEach((r, i) => {
        r.index = i;
      });
      setEventTypeRules([...eventTypeRules]);
      makeDirty();
    },
    [eventTypeRules, makeDirty]
  );

  const addAccountFieldMember = useCallback(
    changeObject => {
      const ruleField = getAccountField(changeObject);
      ruleField.members.push(createEmptyMember());
      makeDirty();
    },
    [getAccountField, makeDirty]
  );

  const addCustomFieldMember = useCallback(
    changeObject => {
      const ruleField = getCustomField(changeObject);
      ruleField.members.push(createEmptyMember());
      makeDirty();
    },
    [getCustomField, makeDirty]
  );

  const removeAccountFieldMember = useCallback(
    changeObject => {
      const ruleField = getAccountField(changeObject);
      ruleField.members.splice(changeObject.memberIndex, 1);
      makeDirty();
    },
    [getAccountField, makeDirty]
  );

  const removeCustomFieldMember = useCallback(
    changeObject => {
      const ruleField = getCustomField(changeObject);
      ruleField.members.splice(changeObject.memberIndex, 1);
      makeDirty();
    },
    [getCustomField, makeDirty]
  );

  const changeValue = useCallback(
    (props, newValue) => {
      if (props.transactionType) {
        changeAccountFieldValue(
          createAccountObjectChangeObject(
            props.rule,
            props.transactionType,
            props.objectIndex,
            props.memberIndex,
            newValue
          )
        );
      } else {
        changeCustomFieldValue(
          createCustomFieldChangeObject(
            props.rule,
            props.fieldName,
            props.memberIndex,
            newValue
          )
        );
      }
    },
    [changeAccountFieldValue, changeCustomFieldValue]
  );

  const changeSource = useCallback(
    (props, newSource) => {
      if (props.transactionType) {
        changeAccountFieldSource(
          createAccountObjectChangeObject(
            props.rule,
            props.transactionType,
            props.objectIndex,
            props.memberIndex,
            newSource
          )
        );
      } else {
        changeCustomFieldSource(
          createCustomFieldChangeObject(
            props.rule,
            props.fieldName,
            props.memberIndex,
            newSource
          )
        );
      }
    },
    [changeAccountFieldSource, changeCustomFieldSource]
  );

  const changeDelimiter = useCallback(
    (props, newDelimiter) => {
      if (props.transactionType) {
        changeAccountFieldDelimiter(
          createAccountObjectChangeObject(
            props.rule,
            props.transactionType,
            props.objectIndex,
            props.memberIndex,
            newDelimiter
          )
        );
      } else {
        changeCustomFieldDelimiter(
          createCustomFieldChangeObject(
            props.rule,
            props.fieldName,
            props.memberIndex,
            newDelimiter
          )
        );
      }
    },
    [changeAccountFieldDelimiter, changeCustomFieldDelimiter]
  );

  const addFieldMember = useCallback(
    props => {
      if (props.transactionType) {
        addAccountFieldMember(
          createAccountObjectChangeObject(
            props.rule,
            props.transactionType,
            props.objectIndex,
            null,
            null
          )
        );
      } else {
        addCustomFieldMember(
          createCustomFieldChangeObject(props.rule, props.fieldName, null, null)
        );
      }
    },
    [addAccountFieldMember, addCustomFieldMember]
  );

  const removeFieldMember = useCallback(
    (props, memberIndex) => {
      if (props.transactionType) {
        removeAccountFieldMember(
          createAccountObjectChangeObject(
            props.rule,
            props.transactionType,
            props.objectIndex,
            memberIndex,
            null
          )
        );
      } else {
        removeCustomFieldMember(
          createCustomFieldChangeObject(
            props.rule,
            props.fieldName,
            memberIndex,
            null
          )
        );
      }
    },
    [removeAccountFieldMember, removeCustomFieldMember]
  );

  const getField = useCallback(
    props =>
      props.transactionType
        ? props.rule[props.transactionType].objectMappings.find(
            m => m.object.index === props.objectIndex
          ).field
        : props.rule[props.fieldName],
    []
  );

  return {
    eventTypeRules,
    setEventTypeRules,
    fieldsAndTemplates,
    setFieldsAndTemplates,
    changeAccountNumber,
    addRule,
    deleteRule,
    templateName,
    setTemplateName,
    dirtyState,
    setDirtyState,
    changeValue,
    changeSource,
    changeDelimiter,
    addFieldMember,
    removeFieldMember,
    getField,
  };
};

export const TemplateContainer = createContainer(useTemplateStore);
